/**
 * Générateur — Il interprète les paramètres d'une fonction affine suivant
 * l'allure de sa courbe représentative.
 * Attendu ID : 227 | Niveau : 3ème | Domaine : Fonctions (d13, O15)
 *
 * Source doc prof (B3S2 + Créé n°9). Plan auto-validé 2026-07-16 (TODO §6) :
 * « classer » (linéaires / affines non linéaires / non affines), « taux »
 * (le taux d'accroissement constant = a), « determiner » (f affine par deux
 * points : a puis b), « portrait » (relier fonction ↔ allure de la droite —
 * le Créé n°9, sans dépendance à une figure).
 */

import { randomChoice, randInt, shuffle, tex, makeClasser, makeRelier, makeTexteTrou } from '../../../utils';
import { fmtAffine, NOMS_FN } from './fonctions';

const ATTENDU_ID = 227;
const L_CLASSER = 'Classer linéaires, affines, non affines';
const L_TAUX = 'Le taux d\'accroissement d\'une fonction affine';
const L_DETERMINER = 'Déterminer une fonction affine par deux points';
const L_PORTRAIT = 'Associer chaque fonction à l\'allure de sa droite';
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const coefNonNul = (lo, hi) => { let c = 0; while (c === 0) c = randInt(lo, hi); return c; };

// ═════════ « classer » ═════════

const genClasserClasser = () => {
    const vus = new Set();
    const unique = (txt) => { if (vus.has(txt)) return null; vus.add(txt); return txt; };
    const lineaires = []; const affines = []; const autres = [];
    for (let essai = 0; essai < 120 && lineaires.length < 2; essai++) {
        const t = randomChoice([
            () => tex(fmtAffine(coefNonNul(-7, 7), 0)),
            () => tex(`\\dfrac{x}{${randInt(2, 7)}}`),
            // Linéaire RÉDUCTIBLE par construction : k(x − m) + k·m = kx.
            () => { const k = coefNonNul(2, 5); const m = randInt(1, 4); return tex(`${k}\\left(x - ${m}\\right) + ${k * m}`); },
        ])();
        if (unique(t)) lineaires.push(t);
    }
    for (let essai = 0; essai < 120 && affines.length < 2; essai++) {
        const t = tex(fmtAffine(coefNonNul(-6, 6), coefNonNul(-8, 8)));
        if (unique(t)) affines.push(t);
    }
    for (let essai = 0; essai < 120 && autres.length < 2; essai++) {
        const t = randomChoice([
            () => tex(`${coefNonNul(-4, 4)}x^{2}`),
            () => tex(`\\dfrac{${randInt(2, 9)}}{x}`),
            () => tex(`x^{2} ${randInt(0, 1) ? '+' : '-'} ${randInt(1, 9)}`),
        ])();
        if (unique(t)) autres.push(t);
    }
    for (const def of ['5x', '\\dfrac{x}{3}', '7x']) {
        const t = tex(def);
        if (lineaires.length < 2 && !vus.has(t)) { vus.add(t); lineaires.push(t); }
    }
    const payload = makeClasser([
        // « (droite par l'origine) » est la CARACTÉRISATION graphique d'une
        // fonction linéaire : la donner classait la moitié des items.
        { name: 'Linéaire', items: lineaires },
        { name: 'Affine non linéaire', items: affines },
        { name: 'Non affine', items: autres },
    ], '<p><b>Classe chaque expression de fonction dans la bonne famille.</b> '
        + 'Réduis l\'expression d\'abord si besoin.</p>', opts(L_CLASSER));
    payload._meta.params = { lineaires, affines, autres };
    return payload;
};

// ═════════ « taux » — [h(x1) − h(x2)]/(x1 − x2) = a ═════════

const genTauxTrous = () => {
    const nom = randomChoice(NOMS_FN);
    const a = coefNonNul(-8, 8); const b = coefNonNul(-9, 9);
    let x1; let x2; let x3; let x4;
    do { x1 = randInt(-5, 6); x2 = randInt(-5, 6); x3 = randInt(-5, 6); x4 = randInt(-5, 6); }
    while (x1 === x2 || x3 === x4);
    const h = (x) => a * x + b;
    // Ni l'expression ${nom}(x) = ax + b ni la propriété « le taux vaut a » ne
    // sont données : elles télégraphiaient deux fois la réponse (lecture directe
    // du coefficient, puis recopie du 1er trou dans le 2d).
    const taux = (u, v) => `\\dfrac{${nom}(${u}) - ${nom}(${v})}{${u} - \\left(${v}\\right)}`;
    const phrase = `$${taux(x1, x2)} = {{${a}}}$\n$${taux(x3, x4)} = {{${a}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${nom} est une fonction affine. On connaît quatre de ses valeurs : `
        + `${tex(`${nom}(${x1}) = ${h(x1)}`)} ; ${tex(`${nom}(${x2}) = ${h(x2)}`)} ; `
        + `${tex(`${nom}(${x3}) = ${h(x3)}`)} ; ${tex(`${nom}(${x4}) = ${h(x4)}`)}.</p>`
        + '<p><b>Calcule les deux taux d\'accroissement.</b></p>',
        { ...opts(L_TAUX), mode: 'saisie' });
    payload._meta.params = { nom, a, b, x1, x2, x3, x4 };
    return payload;
};

// ═════════ « determiner » — f affine par deux points ═════════

const genDeterminerTrous = () => {
    const nom = randomChoice(NOMS_FN);
    let a; let b; let x1; let x2;
    do {
        a = coefNonNul(-6, 6); b = randInt(-8, 8);
        x1 = randInt(-4, 4); x2 = randInt(-4, 4);
    } while (x1 === x2);
    const y1 = a * x1 + b; const y2 = a * x2 + b;
    // Deux trous a et b suffisent : phrase SIMPLE, sans redondance ambiguë.
    const phraseSimple = `$a = {{${a}}} \\qquad b = {{${b}}}$`;
    const payload = makeTexteTrou(phraseSimple,
        `<p>${nom} est une fonction affine de la forme ${tex(`${nom}(x) = ax + b`)}, telle que `
        + `${tex(`${nom}(${x1}) = ${y1}`)} et ${tex(`${nom}(${x2}) = ${y2}`)}.</p>`
        // La formule du taux d'accroissement et « remplace dans ax + b » étaient
        // les deux étapes de la méthode.
        + '<p><b>Calcule a, puis b.</b> Écris chaque valeur avec son signe.</p>',
        { ...opts(L_DETERMINER), mode: 'saisie' });
    payload._meta.params = { nom, a, b, x1, x2 };
    return payload;
};

// ═════════ « portrait » — relier fonction ↔ allure (Créé n°9) ═════════

const genPortraitRelier = () => {
    const b = randInt(1, 5);
    const aPos = randInt(2, 6); const aNeg = -randInt(2, 6);
    const cst = randInt(1, 9);
    const pairs = [
        { source: tex(`f(x) = ${aPos}x + ${b}`), target: `droite qui monte, passant par (0 ; ${b})` },
        { source: tex(`g(x) = ${aNeg}x + ${b}`), target: `droite qui descend, passant par (0 ; ${b})` },
        { source: tex(`h(x) = ${aPos}x`), target: 'droite qui monte, passant par l\'origine' },
        { source: tex(`k(x) = ${cst}`), target: 'droite horizontale' },
    ];
    const payload = makeRelier(shuffle(pairs),
        // « Le signe de a donne le sens, b donne le point sur l'axe des
        // ordonnées » était la règle d'association tout entière.
        '<p><b>Associe chaque fonction à la description de sa droite représentative.</b></p>',
        opts(L_PORTRAIT));
    payload._meta.params = { b, aPos, aNeg, cst };
    return payload;
};

const GENERATORS = {
    classer_classer: genClasserClasser,
    taux_texte_trou_saisie: genTauxTrous,
    determiner_texte_trou_saisie: genDeterminerTrous,
    portrait_relier: genPortraitRelier,
};
const FORMAT_LABELS = { classer: 'Classer', texte_trou_saisie: 'Saisie trous', relier: 'Relier' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));
export const CATEGORIES = [
    { id: 'classer', label: L_CLASSER, formatsList: buildFormats('classer') },
    { id: 'taux', label: L_TAUX, formatsList: buildFormats('taux') },
    { id: 'determiner', label: L_DETERMINER, formatsList: buildFormats('determiner') },
    { id: 'portrait', label: L_PORTRAIT, formatsList: buildFormats('portrait') },
];
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
